<template>
    <div>
        <div class="lib-list">
            <questionnaire-card :questionnaire="basicQuestionnaire" @click="showBasicQuestionnaire" />
            <template v-if="!loading">
                <questionnaire-card v-for="questionnaire in questionnaires" :key="questionnaire.objectID"
                    :questionnaire="questionnaire" with-menu
                    @click:delete="showDeleteQuestionnaire(questionnaire.objectID)"
                    @click:copy="confirmCopy(questionnaire)" @click:folder="showSaveFolder(questionnaire)"
                    @click:edit="showEditQuestionnaireDialog(questionnaire)" @mousedown="startDrag"
                    @click:share="showShareQuestionnaireDialog(questionnaire)" :canShare="canShare" />
            </template>
        </div>

        <skeleton-loader v-if="loading" type="lib-rows" :count="perPage" class="mt-2" />

        <kimsa-pagination class="mt-4" @page="onPageChange" :value="page + 1" :pageCount="pageCount" center
            :total-visible="isMobile ? 4 : (totalVisiblePagination || 7)" />

        <save-in-folder-dialog ref="saveInFolder" :current-id="questionnaireId" :default-folder="folderId"
            @cancel="questionnaireId = null; folderId = null" type="questionnaires" />

        <create-questionnaire-dialog :coachId="coachId" ref="createQuestionnaire" @success="onQuestionnaireSaved" />

        <questionnaire-card v-if="dragging" :questionnaire="draggingItem" with-menu class="floating-drag-item" />

        <delete-dragging-button :dragging="dragMoved" @mouseup="showDeleteQuestionnaire(draggingItem.objectID)" />

        <kimsa-confirm ref="showDelete" :title="$t('questionnaires.delete_title')"
            :content="$t('questionnaires.confirm_deleted')" :persistent="false" :loading="loadingDelete"
            @confirm="deleteQuestionnaire" @cancel="questionnaireId = null" :close-on-confirm="false"
            :alert-mode="true" />

        <share-questionnaire-dialog ref="share" />

        <kimsa-confirm ref="confirmCopy" :title="$t('common.duplicate')" :content="$t('questionnaires.confirm_duplicate')"
            :persistent="false" :loading="loadingCopy" @confirm="onCopyQuestionnaire" @cancel="copyItem = null"
            :close-on-confirm="false" />
    </div>
</template>

<script>
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { QuestionnaireIndex } from "@/utils/Algolia";
import { db, Timestamp } from "@/utils/Firebase";
import { debounce } from "@/utils/Common";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import { mapActions } from "vuex";
import SaveInFolderDialog from "@/components/coach/SaveInFolderDialog";
import KimsaPagination from "@/components/common/KimsaPagination";
import DeleteDraggingButton from '../../common/DeleteDraggingButton.vue';
import draggingMixin from "../../../mixins/draggingMixin";
import QuestionnaireCard from '../../common/routines/QuestionnaireCard.vue';
import CreateQuestionnaireDialog from './CreateQuestionnaireDialog.vue';
import { getQuestions } from "../../../utils/LocalData";
import ShareQuestionnaireDialog from './ShareQuestionnaireDialog.vue';
import algoliaInlineItemMixin from '../../../mixins/algoliaInlineItemMixin';

export default {
    name: "Questionnaires",
    mixins: [draggingMixin, algoliaInlineItemMixin],
    components: { KimsaPagination, SaveInFolderDialog, SkeletonLoader, KimsaConfirm, DeleteDraggingButton, QuestionnaireCard, CreateQuestionnaireDialog, ShareQuestionnaireDialog },
    props: {
        search: {
            type: String,
            default: '',
        },
        folder: {
            type: String,
            default: '',
        },
        totalVisiblePagination: {
            type: [Number, String],
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            loadingDelete: false,
            questionnaireId: null,
            questionnaires: [],
            coaches: [],
            folderId: null,
            page: 0,
            pageCount: 0,
            perPage: 6,
            copyItem: null,
            loadingCopy: false,
            basicQuestionnaire: {
                name: this.$t('questionnaires.basic'),
                questions: getQuestions().slice(0, 5),
                readonly: true,
            }
        }
    },
    computed: {
        companyId(vm = this) {
            return vm.account?.coachSubscription?.companyId || vm.currentUser.uid
        },
        canShare() {
            return this.isCompany || this.account?.accountType === 'coach_company'
        },
    },
    async mounted() {
        let vm = this
        vm.isCompany ? await vm.loadCoaches() : await vm.loadQuestionnaires()
    },
    methods: {
        ...mapActions(['fetchCompanyCoaches']),
        async loadCoaches() {
            const vm = this
            const coaches = await vm.fetchCompanyCoaches(vm.companyId)
            vm.coaches = [vm.coachId, ...coaches.map(c => c.objectID)]
        },
        async showDeleteQuestionnaire(id) {
            const vm = this
            vm.resetDrag()
            vm.questionnaireId = id
            vm.$refs.showDelete.show()
        },
        async deleteQuestionnaire() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('questionnaires').doc(vm.questionnaireId).delete()
                vm.questionnaires = vm.markDeletedInline(vm.questionnaires, vm.questionnaireId)
                vm.toast(vm.$t('questionnaires.deleted'))
                vm.questionnaireId = null
                vm.$refs.showDelete?.hide()
            }, 'loadingDelete')
        },
        debouncedLoadQuestionnaires: debounce(
            function () {
                this.loadQuestionnaires()
            }, 1000),
        async loadQuestionnaires() {
            const vm = this
            await vm.withLoading(async () => {
                vm.questionnaires = []
                let coaches = vm.coaches.length === 0 ? vm.coachId : vm.coaches.join(' OR coachId:')
                let filter = `coachId: ${coaches}`
                if (vm.folder) filter += ` AND folder: ${vm.folder}`
                let response = await QuestionnaireIndex.search(vm.search, {
                    filters: filter,
                    hitsPerPage: vm.perPage,
                    page: vm.page,
                })
                vm.pageCount = response.nbPages || 0
                vm.questionnaires = vm.filterDeletedInline(response.hits)
            })
        },
        confirmCopy(questionnaire) {
            const vm = this
            vm.resetDrag()
            vm.copyItem = questionnaire
            vm.$refs.confirmCopy.show()
        },
        async onCopyQuestionnaire() {
            const vm = this
            const questionnaire = vm.copyItem
            await vm.withLoading(async () => {
            let currentQuestionnaire = await db.collection('questionnaires').doc(questionnaire.objectID).get()

            let payload = {
                ...currentQuestionnaire.data(),
                coachId: vm.coachId,
                name: `${questionnaire.name} ${vm.$t('common.copy_template')}`
            }
            delete payload.created_at
            delete payload.objectID
            payload = JSON.parse(JSON.stringify(payload))
            let ref = await db.collection('questionnaires').add({ ...payload, used: 0, created_at: Timestamp.now() })

            vm.toast(vm.$t('questionnaires.copied'))
            vm.$refs.confirmCopy?.hide()
            let newItem = { ...payload, used: 0, objectID: ref.id, id: ref.id }
            vm.questionnaires.unshift(newItem)
            vm.showEditQuestionnaireDialog(newItem)
            }, 'loadingCopy')
        },
        showSaveFolder(questionnaire) {
            const vm = this
            vm.questionnaireId = questionnaire.objectID
            vm.folderId = questionnaire.folder || null
            vm.$refs.saveInFolder.show()
        },
        refresh() {
            const vm = this
            vm.loadQuestionnaires()
        },
        onQuestionnaireSaved(questionnaire) {
            const vm = this
            if (!vm.addItemInline(vm.questionnaires, questionnaire, 'folder', vm.folder)) vm.loadQuestionnaires()
        },
        onPageChange(p) {
            const vm = this
            const newPage = Number(p) - 1
            if (newPage === vm.page) return
            vm.page = newPage
            vm.loadQuestionnaires()
        },
        showEditQuestionnaireDialog(questionnaire) {
            const vm = this;
            vm.$refs.createQuestionnaire.setEditingMode(questionnaire)
        },
        handleDoubleClickDragging(item) {
            const vm = this;
            vm.confirmCopy(item)
        },
        handleClickDragging(item) {
            const vm = this;
            vm.showEditQuestionnaireDialog(item)
        },
        showBasicQuestionnaire() {
            const vm = this;
            vm.showEditQuestionnaireDialog(vm.basicQuestionnaire);
        },
        showShareQuestionnaireDialog(questionnaire) {
            const vm = this;
            vm.$refs.share.show(questionnaire)
        }
    },
    watch: {
        coaches() {
            this.loadQuestionnaires()
        },
        search(val) {
            this.page = 0
            this.questionnaires = []
            if (!val) return this.loadQuestionnaires()
            return this.debouncedLoadQuestionnaires()
        },
        loading(val) {
            this.$emit('loading', val)
        },
        folder() {
            this.page = 0
            this.questionnaires = []
            this.refresh()
        }
    }
}
</script>

<style scoped>
.lib-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.lib-empty {
    text-align: center;
    padding: 32px 0;
    color: var(--v-txtMuted-base);
}

.floating-drag-item {
    position: fixed;
    z-index: 9999;
    opacity: var(--drag-opacity);
    width: 250px;
    left: 0;
    top: 0;
    transform: translate(var(--drag-x), var(--drag-y));
    pointer-events: none;
}
</style>